// Item 16: independent formatting zones for filter widgets.
// The widget container keeps the normal widget appearance (Background section); the zones below
// style the internal controls and never inherit the widget background unless `inherit` is on.
//
// options.filter_style = {
//   selection: { inherit, bg, text, border, borderWidth, borderStyle, radius, fontSize, weight, padding,
//                hoverBg, hoverText, activeBg, activeText },
//   dropdown:  { bg, text, border, radius, hoverBg, activeBg, activeText, maxHeight },
//   text:      { color, fontSize, weight, show, fontFamily },   // label / title above the control
//   icon:      { color, size, show },                            // chevron / clear icons
//   buttons:   { bg, text, border, radius, fontSize, weight, hoverBg, hoverText, fontFamily,
//                primaryBg, primaryText },                       // Apply / Clear / Select all row
// }
// Every zone accepts `fontFamily`; dropdown + buttons fall back to the selection area's family.

export const DEFAULT_FILTER_STYLE = {
  selection: {
    inherit: false, fontFamily: "", bg: "", text: "", border: "", borderWidth: 1, borderStyle: "solid", radius: 999,
    fontSize: 12, weight: 500, padding: 8, hoverBg: "", hoverText: "", activeBg: "", activeText: "",
  },
  dropdown: { bg: "", text: "", border: "", radius: 10, hoverBg: "", activeBg: "", activeText: "", maxHeight: 240, checkColor: "", fontFamily: "" },
  text: { color: "", fontSize: 11, weight: 500, show: true, fontFamily: "" },
  icon: { color: "", size: 14, show: true },
  buttons: { bg: "", text: "", border: "", radius: 8, fontSize: 10, weight: 500, hoverBg: "", hoverText: "", primaryBg: "", primaryText: "", fontFamily: "" },
};

export function resolveFilterStyle(opts) {
  const fs = (opts && opts.filter_style) || {};
  const out = {};
  for (const zone of Object.keys(DEFAULT_FILTER_STYLE)) out[zone] = { ...DEFAULT_FILTER_STYLE[zone], ...(fs[zone] || {}) };
  return out;
}

const px = (n, d) => `${Number.isFinite(Number(n)) && n !== "" && n != null ? Number(n) : d}px`;

// CSS custom properties consumed by .fw-* classes (index.css). Apply to the widget root AND to any
// portaled PopoverContent (portals don't inherit the widget's variables).
export function filterStyleVars(opts) {
  const s = resolveFilterStyle(opts);
  const sel = s.selection, dd = s.dropdown, tx = s.text, ic = s.icon, bt = s.buttons;
  const fam = sel.fontFamily || "inherit";
  const selBg = sel.inherit ? "transparent" : (sel.bg || "var(--surface)");
  const selText = sel.text || "var(--ink)";
  return {
    "--fw-sel-bg": selBg,
    "--fw-sel-text": selText,
    "--fw-sel-border": sel.border || "var(--glass-border)",
    "--fw-sel-border-w": px(sel.borderWidth, 1),
    "--fw-sel-border-style": sel.borderStyle || "solid",
    "--fw-sel-radius": px(sel.radius, 999),
    "--fw-sel-font": px(sel.fontSize, 12),
    "--fw-sel-weight": String(sel.weight || 500),
    "--fw-sel-pad": px(sel.padding, 8),
    "--fw-sel-hover-bg": sel.hoverBg || (sel.inherit ? "rgba(0,0,0,0.04)" : "var(--surface-2)"),
    "--fw-sel-hover-text": sel.hoverText || selText,
    "--fw-sel-active-bg": sel.activeBg || "rgba(43,78,255,0.10)",
    "--fw-sel-active-text": sel.activeText || "var(--brand-ocean)",
    "--fw-dd-bg": dd.bg || "var(--surface)",
    "--fw-dd-text": dd.text || "var(--ink-2)",
    "--fw-dd-border": dd.border || "var(--glass-border)",
    "--fw-dd-radius": px(dd.radius, 10),
    "--fw-dd-hover-bg": dd.hoverBg || "var(--surface-2)",
    "--fw-dd-active-bg": dd.activeBg || "rgba(43,78,255,0.10)",
    "--fw-dd-active-text": dd.activeText || "var(--brand-ocean)",
    "--fw-dd-maxh": px(dd.maxHeight, 240),
    "--fw-dd-check": dd.checkColor || "var(--brand-ocean)",
    "--fw-dd-family": dd.fontFamily || fam,
    "--fw-sel-family": fam,
    "--fw-text-color": tx.color || "var(--ink-3)",
    "--fw-text-font": px(tx.fontSize, 11),
    "--fw-text-weight": String(tx.weight || 500),
    "--fw-text-family": tx.fontFamily || fam,
    "--fw-icon-color": ic.color || "var(--ink-3)",
    "--fw-icon-size": px(ic.size, 14),
    "--fw-btn-bg": bt.bg || "transparent",
    "--fw-btn-text": bt.text || "var(--ink-3)",
    "--fw-btn-border": bt.border || "transparent",
    "--fw-btn-radius": px(bt.radius, 8),
    "--fw-btn-font": px(bt.fontSize, 10),
    "--fw-btn-weight": String(bt.weight || 500),
    "--fw-btn-hover-bg": bt.hoverBg || "var(--surface-2)",
    "--fw-btn-hover-text": bt.hoverText || "var(--ink)",
    "--fw-btn-primary-bg": bt.primaryBg || "var(--brand-ocean)",
    "--fw-btn-primary-text": bt.primaryText || "#fff",
    "--fw-btn-family": bt.fontFamily || fam,
  };
}
